import { startOfWeek, addDays, isSameDay, format, isAfter } from "date-fns";
import { laneLayoutForWeek, activityDayRange, isMultiDay } from "@/lib/calendar-layout";

const BAR_HEIGHT = 22;
const BAR_GAP = 3;

export default function CalendarWeek({ currentDate, activities, onActivityClick, onDayClick }) {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    // Multi-day events get rendered in the top spanning band.
    // Single-day events still get grouped per column below.
    const multiDay = activities.filter(a => isMultiDay(a));
    const multiDayLayout = laneLayoutForWeek(multiDay, weekStart);
    const laneCount = multiDayLayout.reduce((n, l) => Math.max(n, l.lane + 1), 0);
    const bandHeight = laneCount > 0 ? laneCount * BAR_HEIGHT + (laneCount - 1) * BAR_GAP + 16 : 0;

    // Per-day grouping for single-day events (by local date).
    const singleDayByCol = Array.from({ length: 7 }, () => []);
    for (const a of activities) {
        if (isMultiDay(a)) continue;
        const [day] = activityDayRange(a);
        const diff = Math.round((day.getTime() - weekStart.getTime()) / 86400000);
        if (diff >= 0 && diff < 7) singleDayByCol[diff].push(a);
    }
    singleDayByCol.forEach(list => list.sort((x, y) => new Date(x.start_time) - new Date(y.start_time)));

    // Day → event IDs (for test attributes: proves multi-day events are "attached" to each day).
    const idsPerCol = Array.from({ length: 7 }, () => []);
    for (const a of activities) {
        for (const d of activityDayRange(a)) {
            const diff = Math.round((d.getTime() - weekStart.getTime()) / 86400000);
            if (diff >= 0 && diff < 7) idsPerCol[diff].push(a.id);
        }
    }

    return (
        <div className="border-t border-l border-[#E5E5E3] bg-white" data-testid="calendar-week">
            {/* Date header */}
            <div className="grid grid-cols-7 border-b border-[#E5E5E3]">
                {days.map((d, i) => {
                    const today = isSameDay(d, new Date());
                    return (
                        <div key={i}
                            onClick={() => onDayClick?.(d)}
                            className="p-4 border-r border-[#E5E5E3] cursor-pointer hover:bg-[#FAFAF9]">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">{format(d, "EEE")}</div>
                            <div className={`font-heading text-2xl font-light tracking-tight mt-1 ${today ? "text-[#111110]" : "text-[#333333]"}`}>
                                {format(d, "d")}
                                {today && <span className="ml-2 text-[10px] tracking-[0.2em] uppercase text-[#111110] border-b border-[#111110] pb-0.5">Today</span>}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Multi-day spanning band */}
            {bandHeight > 0 && (
                <div className="border-b border-[#E5E5E3] relative" style={{ height: bandHeight }} data-testid="week-multiday-band">
                    <div className="absolute inset-0 grid grid-cols-7 pointer-events-none">
                        {days.map((_, i) => (
                            <div key={i} className="border-r border-[#E5E5E3] last:border-r-0" />
                        ))}
                    </div>
                    <div
                        className="absolute inset-0 grid grid-cols-7 pointer-events-none"
                        style={{ padding: 8, rowGap: BAR_GAP }}
                    >
                        {multiDayLayout.map(({ activity, startCol, span, lane, continuesLeft, continuesRight }) => (
                            <button
                                key={`${activity.id}-${format(weekStart, "yyyyMMdd")}`}
                                onClick={() => onActivityClick?.(activity)}
                                style={{
                                    backgroundColor: activity.color || "#F0F0EE",
                                    gridColumn: `${startCol + 1} / span ${span}`,
                                    gridRow: lane + 1,
                                    height: BAR_HEIGHT,
                                    borderTopLeftRadius: continuesLeft ? 0 : undefined,
                                    borderBottomLeftRadius: continuesLeft ? 0 : undefined,
                                    borderTopRightRadius: continuesRight ? 0 : undefined,
                                    borderBottomRightRadius: continuesRight ? 0 : undefined,
                                }}
                                className={`pointer-events-auto text-left text-xs leading-none px-2 truncate border-l-2 ${continuesLeft ? "border-l-0" : "border-[#111110]/25"} hover:brightness-95 transition-all flex items-center ${activity.is_past ? "opacity-50 line-through decoration-[#888885]/60 text-[#666664]" : "text-[#111110]"}`}
                                data-testid={`week-event-${activity.id}`}
                                data-activity-id={activity.id}
                                data-span={span}
                                title={activity.title}
                            >
                                <span className="truncate">
                                    {continuesLeft ? "… " : ""}{activity.title}{continuesRight ? " …" : ""}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Per-day columns for single-day events */}
            <div className="grid grid-cols-7 min-h-[400px]">
                {days.map((d, i) => {
                    const key = format(d, "yyyy-MM-dd");
                    const list = singleDayByCol[i];
                    return (
                        <div
                            key={i}
                            className="border-r border-[#E5E5E3] p-2 space-y-2"
                            data-testid={`week-col-${key}`}
                            data-event-ids={idsPerCol[i].join(",")}
                        >
                            {list.length === 0 ? (
                                <div className="text-[11px] text-[#BBBBB8] text-center py-4">—</div>
                            ) : list.map(a => (
                                <button
                                    key={a.id}
                                    onClick={() => onActivityClick?.(a)}
                                    style={{ backgroundColor: a.color || "#FFFFFF" }}
                                    className={`w-full text-left border border-[#E5E5E3] p-2 hover:brightness-95 transition-all border-l-2 border-l-[#111110]/40 ${a.is_past ? "opacity-50" : ""}`}
                                    data-testid={`week-event-${a.id}`}
                                    data-activity-id={a.id}
                                >
                                    <div className="text-[10px] tracking-[0.15em] uppercase text-[#666664]">
                                        {format(new Date(a.start_time), "HH:mm")} — {format(new Date(a.end_time), "HH:mm")}
                                        {a.is_past && <span className="ml-2 text-[#888885]">· ended</span>}
                                    </div>
                                    <div className="text-sm text-[#111110] font-medium mt-1 line-clamp-2">{a.title}</div>
                                    {a.location && <div className="text-xs text-[#666664] mt-1 truncate">{a.location}</div>}
                                </button>
                            ))}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
